import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useContent } from "@/lib/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.4 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskLine({ children, i, className }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        custom={i}
        variants={line}
        initial="hidden"
        animate="show"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onJoin }) {
  const { t } = useContent();
  const HERO_IMG = t("media_hero");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden bg-[#090909] text-[#F2EFEB]"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0"
      >
        <motion.img
          src={HERO_IMG}
          alt="skater underground"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.42 }}
          transition={{ duration: 1.4, delay: 0.2 }}
          className="w-full h-full object-cover grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-transparent to-[#090909]/40" />
      </motion.div>

      {/* top ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.6 }}
        className="absolute top-20 left-0 right-0 px-4 md:px-8 flex justify-between overline z-10"
      >
        <span>{t("hero_ticker_left")}</span>
        <span className="hidden sm:block">{t("hero_ticker_center")}</span>
        <span>{t("hero_ticker_right")}</span>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8"
      >
        <MaskLine i={0} className="font-display text-[19vw] md:text-[15vw] leading-[0.82] tracking-tight">
          {t("hero_word1")}
        </MaskLine>
        <MaskLine i={1} className="font-display text-[19vw] md:text-[15vw] leading-[0.82] tracking-tight -mt-2 md:-mt-6">
          {t("hero_word2")}
        </MaskLine>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-4">
          <MaskLine i={2} className="font-serif-ed italic text-2xl md:text-4xl text-[#DFFF00]">
            {t("hero_subtitle")}
          </MaskLine>
          <motion.button
            data-testid="hero-join-btn"
            onClick={onJoin}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.6 }}
            className="overline border-2 border-[#F2EFEB] px-6 py-4 hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors"
          >
            {t("hero_cta")}
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="absolute bottom-6 left-0 right-0 px-4 md:px-8 flex justify-between overline z-10"
      >
        <span>{t("hero_scroll")}</span>
        <span>{t("hero_transit")}</span>
      </motion.div>
    </section>
  );
}
